import React from "react";
import {
  Box,
  Chip,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { buildChainText, computeHealthScore, resultIssueCount } from "./auditUtils";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  searchField: {
    minWidth: 280,
    [theme.breakpoints.down("xs")]: {
      minWidth: "100%",
      width: "100%",
    },
  },
  list: {
    display: "grid",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    maxHeight: 760,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
  },
  card: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 4,
    cursor: "pointer",
    transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: theme.customShadows ? theme.customShadows.widget : "0 8px 20px rgba(0,0,0,0.08)",
    },
  },
  activeCard: {
    boxShadow: theme.customShadows ? theme.customShadows.widgetWide : "0 10px 24px rgba(0,0,0,0.1)",
  },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
  },
  statusBlock: {
    display: "grid",
    justifyItems: "end",
    gap: theme.spacing(0.75),
  },
  metricRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1.25),
  },
  healthTrack: {
    height: 8,
    borderRadius: 999,
    overflow: "hidden",
    backgroundColor: theme.palette.grey[200],
    marginTop: theme.spacing(1.5),
  },
  healthFill: {
    height: "100%",
    borderRadius: 999,
  },
  chainPreview: {
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  issuePreview: {
    marginTop: theme.spacing(1.25),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  caption: {
    color: theme.palette.text.secondary,
  },
}));

function statusColor(theme, status) {
  if (status === "ok") {
    return theme.palette.success.main;
  }

  if (status === "warning") {
    return theme.palette.warning.main;
  }

  if (status === "info") {
    return theme.palette.info.main;
  }

  return theme.palette.error.main;
}

function labelForFilter(filter) {
  if (filter === "broken") {
    return "Needs Attention";
  }

  if (filter === "healthy") {
    return "Healthy";
  }

  return "All Rows";
}

export default function SchainAuditResults({
  filter,
  filteredResults,
  onFilterChange,
  onSearchChange,
  onSelect,
  results,
  searchQuery,
  selectedIndex,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const filterCounts = {
    all: results.length,
    broken: results.filter((result) => !result.supplyChain || result.supplyChain.status !== "ok").length,
    healthy: results.filter((result) => result.supplyChain && result.supplyChain.status === "ok").length,
  };

  return (
    <Box>
      <Box className={classes.toolbar}>
        <Box className={classes.filters}>
          {["all", "broken", "healthy"].map((item) => (
            <Chip
              key={item}
              clickable
              color={filter === item ? "secondary" : "default"}
              label={`${labelForFilter(item)} (${filterCounts[item] || 0})`}
              onClick={() => onFilterChange(item)}
              variant={filter === item ? "default" : "outlined"}
            />
          ))}
        </Box>

        <TextField
          size="small"
          variant="outlined"
          placeholder="Search domains, seller IDs, or issue codes"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Typography variant="body2" className={classes.caption} style={{ marginTop: 12 }}>
        Showing {filteredResults.length} of {results.length} validated row{results.length === 1 ? "" : "s"}.
      </Typography>

      {results.length === 0 ? (
        <Paper variant="outlined" style={{ padding: 24, marginTop: 16 }}>
          <Typography variant="body2" className={classes.caption}>
            No validation results yet. Prepare a dataset and run validation to populate the row explorer.
          </Typography>
        </Paper>
      ) : filteredResults.length === 0 ? (
        <Paper variant="outlined" style={{ padding: 24, marginTop: 16 }}>
          <Typography variant="body2" className={classes.caption}>
            No rows match the current filter and search query.
          </Typography>
        </Paper>
      ) : (
        <Box className={classes.list}>
          {filteredResults.map(({ result, index }) => {
            const chainStatus = result.supplyChain ? result.supplyChain.status : "broken";
            const score = computeHealthScore(result);
            const accent = statusColor(theme, chainStatus);
            const issueCount = resultIssueCount(result);
            const leadIssue = (result.issues || [])[0];

            return (
              <Paper
                key={`${result.targetDomain || "row"}-${index}`}
                variant="outlined"
                className={`${classes.card} ${selectedIndex === index ? classes.activeCard : ""}`}
                style={{
                  borderLeftColor: accent,
                  boxShadow: selectedIndex === index ? `0 0 0 1px ${accent}22` : undefined,
                }}
                onClick={() => onSelect(index)}
              >
                <Box className={classes.cardTop}>
                  <Box>
                    <Typography variant="caption" className={classes.caption}>
                      Row {result.input ? result.input.sourceRowNumber : index + 1}
                    </Typography>
                    <Typography variant="h6" style={{ marginTop: 4 }}>
                      {result.targetDomain || "Unknown inventory"}
                    </Typography>
                    <Typography variant="body2" className={classes.chainPreview}>
                      {buildChainText(result) || "No payment flow could be resolved for this row."}
                    </Typography>
                  </Box>

                  <Box className={classes.statusBlock}>
                    <Chip
                      label={chainStatus === "ok" ? "Healthy" : chainStatus === "warning" ? "Warning" : "Attention"}
                      style={{
                        backgroundColor: `${accent}18`,
                        color: accent,
                        fontWeight: 700,
                      }}
                    />
                    <Typography variant="subtitle2" style={{ color: accent }}>
                      {score}%
                    </Typography>
                  </Box>
                </Box>

                <Box className={classes.metricRow}>
                  <Chip size="small" label={`${issueCount} issue${issueCount === 1 ? "" : "s"}`} variant="outlined" />
                  <Chip
                    size="small"
                    label={`${(result.matchedPairs || []).length} node${(result.matchedPairs || []).length === 1 ? "" : "s"}`}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`${result.supplyChain ? result.supplyChain.edgeErrors || 0 : 0} broken link${result.supplyChain && result.supplyChain.edgeErrors === 1 ? "" : "s"}`}
                    variant="outlined"
                  />
                </Box>

                <Box className={classes.healthTrack}>
                  <Box
                    className={classes.healthFill}
                    style={{
                      width: `${Math.max(score, 6)}%`,
                      backgroundColor: accent,
                    }}
                  />
                </Box>

                {leadIssue ? (
                  <Box className={classes.issuePreview}>
                    <Typography variant="caption" color="textSecondary">
                      Lead issue
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 4 }}>
                      {leadIssue.code}: {leadIssue.message}
                    </Typography>
                  </Box>
                ) : (
                  <Box className={classes.issuePreview}>
                    <Typography variant="body2" style={{ color: theme.palette.success.main }}>
                      No issues surfaced for this row.
                    </Typography>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
